import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes, FaSave, FaPaperclip, FaFileCsv, FaFileExcel, FaFilePdf, FaFile } from 'react-icons/fa';

import useFileReferences from '../../../hooks/useFileReferences';

const emptyState = {
  widget_type: 'kpi',
  dataset: '',          // siempre vacío: la IA decide la fuente
  chart_type: 'bar',
  size: 6,
  title: '',
  prompt: '',
};

const FILE_ICONS = {
  csv: FaFileCsv,
  excel: FaFileExcel,
  xlsx: FaFileExcel,
  pdf: FaFilePdf,
};

const FileChipIcon = ({ type }) => {
  const Icon = FILE_ICONS[(type || '').toLowerCase()] || FaFile;
  return <Icon className="widget-editor-file-chip-icon" />;
};

const WidgetEditorModal = ({
  isOpen,
  mode = 'create',
  initialWidget = null,
  meta = null,
  username = '',
  onClose,
  onSubmit,
  busy = false,
}) => {
  const [form, setForm] = useState(emptyState);
  const textareaRef = useRef(null);

  // Hook compartido con el chat robusto: detección @, dropdown, contenido S3.
  const {
    filteredFiles,
    referencedFiles,
    showFileDropdown,
    selectedFileIndex,
    detectAtReference,
    updateReferencedFiles,
    insertFileReference,
    handleKeyNavigation,
    setShowFileDropdown,
    setSelectedFileIndex,
  } = useFileReferences(username, { enabled: isOpen });

  useEffect(() => {
    if (!isOpen) return;
    if (initialWidget) {
      setForm({
        widget_type: initialWidget.widget_type || 'kpi',
        dataset: initialWidget.dataset || '',
        chart_type: initialWidget.chart_type || 'bar',
        size: initialWidget.size || 6,
        title: initialWidget.title || '',
        prompt: initialWidget.prompt || '',
      });
    } else {
      setForm(emptyState);
    }
  }, [isOpen, initialWidget]);

  // Re-evaluar chips cada vez que cambia el prompt o la lista de archivos disponibles.
  useEffect(() => {
    if (!isOpen) return;
    updateReferencedFiles(form.prompt);
  }, [form.prompt, isOpen, updateReferencedFiles]);

  const widgetTypes = useMemo(() => meta?.widget_types || [], [meta]);
  const chartTypes  = useMemo(() => meta?.chart_types || [], [meta]);
  const sizes       = useMemo(() => meta?.sizes || [], [meta]);

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // -------------------------------------------------------------------------
  // Textarea con detección de @ — usa la misma UX que el chat robusto.
  // -------------------------------------------------------------------------

  const handlePromptChange = useCallback((e) => {
    const value = e.target.value;
    handleChange('prompt', value);
    detectAtReference(value, e.target.selectionStart || value.length);
  }, [detectAtReference]);

  const insertFileAtCursor = useCallback((file) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { newText, newCursorPosition } = insertFileReference(
      file, form.prompt, ta.selectionStart ?? form.prompt.length
    );
    handleChange('prompt', newText);
    // restaurar foco + cursor después del render
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    });
  }, [form.prompt, insertFileReference]);

  const handlePromptKeyDown = useCallback((e) => {
    if (!showFileDropdown) return;
    if (['ArrowUp', 'ArrowDown', 'Escape'].includes(e.key)) {
      const r = handleKeyNavigation(e.key);
      if (r === 'handled') e.preventDefault();
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      const file = handleKeyNavigation('Enter');
      if (file && typeof file === 'object') {
        e.preventDefault();
        insertFileAtCursor(file);
      }
    }
  }, [showFileDropdown, handleKeyNavigation, insertFileAtCursor]);

  const removeFileRef = useCallback((file) => {
    // Quitar @nombre del prompt (todas las ocurrencias).
    const escaped = (file.original_name || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escaped) return;
    const cleaned = form.prompt.replace(
      new RegExp(`@${escaped}(?=\\s|$|,|\\.)`, 'gi'),
      ''
    ).replace(/ {2,}/g, ' ').trim();
    handleChange('prompt', cleaned);
  }, [form.prompt]);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const payload = {
      widget_type: form.widget_type,
      dataset: '',  // siempre automático
      size: Number(form.size) || 6,
      title: (form.title || '').trim(),
      prompt: (form.prompt || '').trim(),
    };
    if (form.widget_type === 'chart') {
      payload.chart_type = form.chart_type;
    }
    onSubmit?.(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="widget-editor-overlay" onClick={onClose}>
      <div
        className="widget-editor-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="widget-editor-header">
          <h3>{mode === 'create' ? 'Nuevo widget' : 'Editar widget'}</h3>
          <button type="button" className="widget-editor-close" onClick={onClose} title="Cerrar">
            <FaTimes />
          </button>
        </div>

        <form className="widget-editor-form" onSubmit={handleSubmit}>
          <div className="widget-editor-row">
            <label className="widget-editor-field">
              <span>Título</span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Ej. Competidores activos"
                maxLength={200}
              />
            </label>
          </div>

          <div className="widget-editor-row">
            <label className="widget-editor-field">
              <span>Tipo de widget</span>
              <select
                value={form.widget_type}
                onChange={(e) => handleChange('widget_type', e.target.value)}
              >
                {widgetTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </label>

            {form.widget_type === 'chart' && (
              <label className="widget-editor-field">
                <span>Tipo de gráfica</span>
                <select
                  value={form.chart_type}
                  onChange={(e) => handleChange('chart_type', e.target.value)}
                >
                  {chartTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="widget-editor-field">
              <span>Tamaño</span>
              <select
                value={form.size}
                onChange={(e) => handleChange('size', e.target.value)}
              >
                {sizes.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="widget-editor-row">
            <label className="widget-editor-field widget-editor-field-full">
              <span>¿Qué querés ver en este widget?</span>
              <div className="widget-editor-prompt-wrap">
                <textarea
                  ref={textareaRef}
                  rows={5}
                  value={form.prompt}
                  onChange={handlePromptChange}
                  onKeyDown={handlePromptKeyDown}
                  onBlur={() => setTimeout(() => setShowFileDropdown(false), 150)}
                  placeholder="Ej: Los 10 productos con mayor diferencia de precio vs la competencia"
                />

                {showFileDropdown && filteredFiles.length > 0 && (
                  <ul className="widget-editor-file-dropdown" role="listbox">
                    {filteredFiles.map((f, idx) => {
                      const isSel = idx === selectedFileIndex;
                      return (
                        <li
                          key={f.id}
                          role="option"
                          aria-selected={isSel}
                          className={`widget-editor-file-option ${isSel ? 'selected' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            insertFileAtCursor(f);
                          }}
                          onMouseEnter={() => setSelectedFileIndex(idx)}
                        >
                          <FileChipIcon type={f.file_type} />
                          <span className="widget-editor-file-option-name">{f.original_name}</span>
                          {f.file_size_formatted && (
                            <span className="widget-editor-file-option-size">{f.file_size_formatted}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <small className="widget-editor-prompt-hint">
                <FaPaperclip /> Tip: escribí <code>@</code> para referenciar un archivo cargado.
              </small>

              {referencedFiles.length > 0 && (
                <div className="widget-editor-file-chips">
                  {referencedFiles.map(f => (
                    <span key={f.id} className="widget-editor-file-chip" title={f.original_name}>
                      <FileChipIcon type={f.file_type} />
                      <span className="widget-editor-file-chip-name">{f.original_name}</span>
                      <button
                        type="button"
                        className="widget-editor-file-chip-remove"
                        onClick={() => removeFileRef(f)}
                        title="Quitar referencia"
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </label>
          </div>

          <div className="widget-editor-footer">
            <button
              type="button"
              className="widget-editor-cancel"
              onClick={onClose}
              disabled={busy}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="widget-editor-save"
              disabled={busy || !form.prompt.trim() || !form.title.trim()}
            >
              <FaSave /> {mode === 'create' ? 'Crear y generar' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WidgetEditorModal;
